import { useState, useEffect, useRef, useCallback } from "react";
import { debounce } from "@excalidraw/excalidraw/utils";
import { uuid4 } from "@sentry/core";
import { getAllExistingSessions, patchSession } from "../storage/database";
import { STORAGE_KEYS } from "../app_constants";
import type { SessionType } from "../app-language/session-state";
import type { ExcalidrawImperativeAPI } from "@excalidraw/excalidraw/types";

const SESSION_NAME_STORAGE_KEY = "excalidraw-session-name";
const SAVE_DEBOUNCE_MS = 1000;
const SCROLL_TO_CONTENT_DELAY_MS = 200;
const BACKEND_URL =
  "https://excalidraw-joey-backend.netlify.app/.netlify/functions/app";

/**
 * Builds the payload object for patching a session to the backend.
 */
const buildSessionPayload = (
  name: string,
  sessionId: string,
  content: string,
) => ({
  [name]: { sessionName: name, sessionId, content },
});

/**
 * Custom hook that encapsulates all session management logic:
 * - Loading/switching sessions
 * - Auto-saving (debounced, triggered via `onContentChange`)
 * - Persisting current session name to localStorage
 * - Flushing unsaved data on `beforeunload`
 */
export const useSessionManager = (
  excalidrawAPI: ExcalidrawImperativeAPI | null,
) => {
  // ---- State ----
  const [sessionName, setSessionName] = useState(
    () => localStorage.getItem(SESSION_NAME_STORAGE_KEY) || "",
  );
  const [sessions, setSessions] = useState<SessionType[]>([]);

  // ---- Refs (for use in callbacks/closures without stale values) ----
  const sessionsRef = useRef<SessionType[]>(sessions);
  const sessionNameRef = useRef(sessionName);
  const loadingFinished = useRef(false);

  useEffect(() => {
    sessionsRef.current = sessions;
  }, [sessions]);

  useEffect(() => {
    sessionNameRef.current = sessionName;
    if (sessionName) {
      localStorage.setItem(SESSION_NAME_STORAGE_KEY, sessionName);
    }
  }, [sessionName]);

  // ---- Fetch sessions from backend ----
  const updateSessionsFromDatabase = useCallback(() => {
    return getAllExistingSessions()
      .then((sessionList) => {
        const parsed: SessionType[] = Object.keys(sessionList).map((key) => ({
          sessionId: sessionList[key].sessionId,
          sessionName: sessionList[key].sessionName,
          content: sessionList[key].content,
        }));
        setSessions(parsed);
      })
      .catch((error) => {
        console.error("Failed to fetch sessions:", error);
      });
  }, []);

  // Auto-select first session when sessions load and none is selected
  useEffect(() => {
    if (sessions.length > 0 && sessionName.length === 0) {
      setSessionName(sessions[0].sessionName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on sessions change
  }, [sessions]);

  // Fetch sessions on mount
  useEffect(() => {
    updateSessionsFromDatabase();
  }, [updateSessionsFromDatabase]);

  // ---- Load session into localStorage & trigger scene reload ----
  const loadSessionIntoStorage = useCallback((session: SessionType) => {
    if (
      session.content ===
      localStorage.getItem(STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS)
    ) {
      loadingFinished.current = true;
      return;
    }
    localStorage.setItem(STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS, session.content);
    document.dispatchEvent(new Event("visibilitychange"));
    loadingFinished.current = true;
  }, []);

  // When sessionName changes, load the corresponding session & scroll to content
  useEffect(() => {
    const session = sessions.find((s) => s.sessionName === sessionName);
    if (session) {
      loadSessionIntoStorage(session);
      setTimeout(() => {
        excalidrawAPI?.scrollToContent(undefined, { animate: true });
      }, SCROLL_TO_CONTENT_DELAY_MS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on session switch
  }, [sessionName]);

  // ---- Debounced save (triggered from onChange) ----
  const saveSessionToBackend = useRef(
    debounce(async () => {
      const value = localStorage.getItem(STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS);
      const name = sessionNameRef.current;
      if (
        !loadingFinished.current ||
        !value ||
        value.length === 0 ||
        name.length === 0
      ) {
        return;
      }
      const currentSession = sessionsRef.current.find(
        (s) => s.sessionName === name,
      );
      try {
        await patchSession(
          buildSessionPayload(
            name,
            currentSession?.sessionId ?? uuid4(),
            value,
          ),
        );
        setSessions((prev) =>
          prev.map((s) =>
            s.sessionName === name ? { ...s, content: value } : s,
          ),
        );
      } catch (error) {
        console.error("Failed to save session:", error);
      }
    }, SAVE_DEBOUNCE_MS),
  ).current;

  // ---- Flush save on page unload ----
  useEffect(() => {
    const flushSessionSave = () => {
      const value = localStorage.getItem(STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS);
      const name = sessionNameRef.current;
      if (!value || value.length === 0 || name.length === 0) {
        return;
      }
      const currentSession = sessionsRef.current.find(
        (s) => s.sessionName === name,
      );
      fetch(`${BACKEND_URL}/patchSession`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-secure-key": import.meta.env.VITE_SECURE_KEY || "SECURE_KEY",
        },
        body: JSON.stringify(
          buildSessionPayload(
            name,
            currentSession?.sessionId ?? uuid4(),
            value,
          ),
        ),
        keepalive: true,
      });
    };

    window.addEventListener("beforeunload", flushSessionSave);
    return () => {
      window.removeEventListener("beforeunload", flushSessionSave);
    };
  }, []);

  return {
    sessionName,
    setSessionName,
    sessions,
    setSessions,
    updateSessionsFromDatabase,
    saveSessionToBackend,
  };
};
